import { LockIcon, SettingsIcon } from 'lucide-react';
import type { ReactElement } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { useChatBranding } from '../ChatProvider';

interface MicPermissionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MicPermissionDialog({
  open,
  onOpenChange,
}: MicPermissionDialogProps): ReactElement {
  const branding = useChatBranding();
  const appName = branding.title;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <LockIcon className="text-info size-5" aria-hidden />
            <DialogTitle>Como liberar o acesso ao microfone</DialogTitle>
          </div>
          <DialogDescription className="sr-only">
            Passo a passo para permitir o microfone nas configurações do navegador.
          </DialogDescription>
        </DialogHeader>

        <div className="border-warning-border bg-warning-bg flex gap-3 rounded-md border p-3">
          <SettingsIcon className="text-warning mt-0.5 size-5 shrink-0" aria-hidden />
          <div>
            <p className="text-warning-fg text-sm font-semibold">
              Microfone bloqueado pelas configurações do navegador
            </p>
            <p className="text-warning-fg/90 mt-1 text-xs">
              Para usar a funcionalidade de gravação de voz no {appName}, você precisa permitir o
              acesso ao microfone.
            </p>
          </div>
        </div>

        <ol className="flex flex-col gap-3">
          <Step n={1} title="Localize o ícone de configurações na barra do navegador">
            Procure pelo ícone 🔒 ou ⚙️ ao lado do endereço da página.
          </Step>
          <Step n={2} title="Clique no ícone de configurações">
            Um menu de permissões do site será exibido.
          </Step>
          <Step n={3} title='Encontre a opção "Microfone"'>
            Pode estar na seção de permissões ou de privacidade.
          </Step>
          <Step n={4} title='Altere para "Permitir"'>
            Substitua "Bloquear" ou "Perguntar" por "Permitir".
          </Step>
          <Step n={5} title="Recarregue a página">
            Atualize o {appName} para que a nova permissão seja aplicada.
          </Step>
        </ol>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Fechar tutorial
          </Button>
          <Button
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Entendi, vou tentar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Step({
  n,
  title,
  children,
}: {
  n: number;
  title: string;
  children: React.ReactNode;
}): ReactElement {
  return (
    <li className="flex gap-3">
      <span
        aria-hidden
        className="bg-info text-primary-foreground inline-flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold tabular-nums"
      >
        {n}
      </span>
      <div className="text-sm leading-snug">
        <p className="font-semibold">{title}</p>
        <p className="text-muted-foreground mt-0.5 text-xs">{children}</p>
      </div>
    </li>
  );
}
